import { resolveLinearName } from 'core/slices/utils/aisleNames';
import CloseIcon from '@mui/icons-material/Close';
import ExploreIcon from '@mui/icons-material/Explore';
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { scrollbarStyles } from '../../styleSheet';
import BaseModal from '../templates/BaseModal';
import AppText from '../utils/AppText';

const cleanMessage = text => text?.replace(/<b>/g, '').replace(/<\/b>/g, '').replace(/\s+([.,!?])/g, '$1').replace(/\s+/g, ' ').trim() ?? '';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    bgcolor: 'background.paper',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2.5,
    pt: 2.5,
    pb: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
    flexShrink: 0,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIconColor: { fontSize: 20, color: 'text.secondary' },
  headerText: { flexGrow: 1 },
  headerTitle: { fontWeight: 600, color: 'text.primary', lineHeight: 1.3 },
  headerSub: { fontSize: '0.75rem', color: 'text.secondary' },
  closeButton: { color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'transparent' } },
  header: {
    px: 2.5,
    pt: 2,
    pb: 1.5,
    flexShrink: 0,
  },
  percentBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    textAlign: 'center',
    mb: 0,
  },
  progressWrap: {
    pt: 0,
    pb: 1,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    bgcolor: theme => theme.bruno.colors.inactive,
    '& .MuiLinearProgress-bar': {
      borderRadius: 4,
    },
  },
  messageCard: {
    mx: 2.5,
    mb: 1,
    px: 1.75,
    py: 1.5,
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    bgcolor: theme => theme.bruno.chrome.appBarBackground,
  },
  messageIconBox: {
    width: 44,
    height: 44,
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.18)',
  },
  messageIcon: { fontSize: 22, color: 'common.white' },
  tabs: {
    borderBottom: '1px solid',
    borderColor: 'divider',
    minHeight: 44,
    flexShrink: 0,
  },
  tab: {
    fontSize: '0.75rem',
    fontWeight: 700,
    minHeight: 44,
    flex: 1,
    maxWidth: '50%',
  },
  tabLabelLoading: {
    display: 'flex',
    alignItems: 'center',
  },
  listBody: {
    flexGrow: 1,
    overflow: 'auto',
    ...scrollbarStyles,
  },
  aisleItem: {
    px: 3,
    py: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': { bgcolor: 'action.hover' },
    cursor: 'default',
  },
  emptyBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    py: 6,
  },
  sectionLabel: {
    px: 2.5,
    pt: 1,
    pb: 0.5,
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  },
  footer: {
    height: 16,
    flexShrink: 0,
    bgcolor: 'background.paper',
  },
};

function resolveLinearLabel(aisleId, layout) {
  if (!aisleId) return aisleId;
  // The robot reports linears ("C-E-2"), one side of an aisle, not the aisle
  // itself. Indexing the layout by the composed id finds nothing: the layout
  // keys aisles, and nests their linears inside.
  return resolveLinearName(layout, aisleId) ?? aisleId;
}

function AisleItem({ aisleId, statusText, layout }) {
  if (!aisleId) return null;
  const aisleName = resolveLinearLabel(aisleId, layout);

  return (
    <Box sx={styles.aisleItem}>
      <AppText variant="body" className="font-bold">{aisleName}</AppText>
      {statusText && (
        <AppText variant="bodySmall" style={{ maxWidth: '55%', textAlign: 'right' }}>{statusText}</AppText>
      )}
    </Box>
  );
}

export default function NavigationStatusModal({
  open,
  onClose,
  navigationProgress,
  navigationCompleted,
  navigationPending,
  navigationLoading,
  aislesLayout,
  currentStore,
  getLayout,
  botMessages,
}) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = React.useState(0);

  useEffect(() => {
    if (open) setTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (currentStore) getLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore]);

  const layout = aislesLayout || {};
  const completed = (navigationCompleted?.aisle || []).filter(Boolean);
  const completedTotal = navigationCompleted?.total ?? 0;
  const pending = (navigationPending?.aisle || []).filter(item => item?.aisle);
  const pendingTotal = navigationPending?.total ?? 0;
  const total = completedTotal + pendingTotal;
  const percent = Math.round((navigationProgress || 0) * 100);
  // Select language from the multi-language response stored in Redux
  const msgs = botMessages?.[i18n.language] ?? botMessages?.['en'] ?? botMessages ?? {};
  const tasksMessage = cleanMessage(msgs.tasks_message);
  const tasksIcon = msgs.tasks_icon || 'information-circle-outline';

  const mapAisleStatus = (status, reasonCode) => {
    if (status === 'not_navigated') {
      return t(`bruno.aisleStatus.${reasonCode}`, {
        defaultValue: t('bruno.aisleStatus.default'),
      });
    }
    return t(`bruno.aisleStatus.${status}`, { defaultValue: status });
  };

  return (
    <BaseModal open={open} onClose={onClose} dialogWidth={720} dialogHeight="88vh">
      <Box sx={styles.root}>

        {/* Header row — icon + title + close */}
        <Box sx={styles.headerRow}>
          <Box sx={styles.headerIcon}>
            <ExploreIcon sx={styles.headerIconColor} />
          </Box>
          <Box sx={styles.headerText}>
            <Typography variant="body1" sx={styles.headerTitle}>
              {t('bruno.navigation.status_title')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Progress block */}
        <Box sx={styles.header}>
          {/* Percent + subtitle */}
          <Box sx={styles.percentBlock}>
            {navigationLoading ? (
              <>
                <Skeleton variant="text" width={100} height={60} />
                <Skeleton variant="text" width={160} height={20} />
              </>
            ) : (
              <>
                <AppText variant="heroNumber" className="text-success">{`${percent}%`}</AppText>
                <AppText variant="subtitle" as="p" style={{ marginTop: 2 }}>
                  {`${t('bruno.navigation.status_sub1')} ${total} ${t('bruno.navigation.status_sub2')}`}
                </AppText>
              </>
            )}
          </Box>

          {/* Progress bar with robot icon above tracking progress */}
          <Box sx={styles.progressWrap}>
            {navigationLoading ? (
              <Skeleton variant="rounded" height={8} sx={{ borderRadius: 4 }} />
            ) : (
              <>
                <Box sx={{ position: 'relative', height: 56, mb: 0.5 }}>
                  <Box
                    component="img"
                    src="/robotBruno.png"
                    alt="Bruno"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: `${Math.max(3, Math.min(percent, 97))}%`,
                      transform: 'translateX(-50%)',
                      height: 56,
                      width: 'auto',
                      objectFit: 'contain',
                      filter: 'brightness(0) saturate(100%) invert(14%) sepia(20%) saturate(600%) hue-rotate(170deg) brightness(90%)',
                    }}
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={percent}
                  color="success"
                  sx={styles.progressBar}
                />
              </>
            )}
          </Box>
        </Box>

        {tasksMessage ? (
          <Box sx={styles.messageCard}>
            <Box sx={styles.messageIconBox}>
              <IconComponent iconName={tasksIcon} style={{ fontSize: 22, color: '#fff' }} />
            </Box>
            <AppText variant="inverseBodySub" as="p" style={{ lineHeight: 1.6 }}>
              {tasksMessage}
            </AppText>
          </Box>
        ) : null}

        {/* Section label */}
        <Box sx={styles.sectionLabel}>
          <AppText variant="capsLabel">
            {t('bruno.tasks.aisles')}
          </AppText>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="inherit"
          indicatorColor="primary"
          sx={styles.tabs}
        >
          <Tab
            label={
              navigationLoading ? (
                <Box sx={styles.tabLabelLoading}>
                  {t('bruno.navigation.tab_scans')}
                  <Skeleton variant="rounded" width={24} height={14} sx={{ ml: 0.5 }} />
                </Box>
              ) : (
                `${t('bruno.navigation.tab_scans')} (${completedTotal})`
              )
            }
            sx={styles.tab}
          />
          <Tab
            label={
              navigationLoading ? (
                <Box sx={styles.tabLabelLoading}>
                  {t('bruno.navigation.tab_no_scans')}
                  <Skeleton variant="rounded" width={24} height={14} sx={{ ml: 0.5 }} />
                </Box>
              ) : (
                `${t('bruno.navigation.tab_no_scans')} (${pendingTotal})`
              )
            }
            sx={styles.tab}
          />
        </Tabs>

        {/* List body */}
        <Box sx={styles.listBody}>
          {navigationLoading ? (
            [...Array(16)].map((_, i) => (
              <React.Fragment key={i}>
                <Box sx={{ px: 3, py: 1 }}>
                  <Skeleton variant="text" width={`${[40, 55, 30, 60, 45, 35, 50, 38, 52, 42, 58, 33, 48, 62, 36, 44][i % 16]}%`} height={20} />
                </Box>
                <Divider sx={{ mx: 3 }} />
              </React.Fragment>
            ))
          ) : tab === 0 ? (
            completed.length === 0 ? (
              <Box sx={styles.emptyBox}>
                <AppText variant="bodySecondary">{t('bruno.navigation.empty_list')}</AppText>
              </Box>
            ) : (
              completed.map((aisleId, i) => (
                <React.Fragment key={aisleId}>
                  <AisleItem aisleId={aisleId} layout={layout} />
                  {i < completed.length - 1 && <Divider sx={{ mx: 3 }} />}
                </React.Fragment>
              ))
            )
          ) : (
            pending.length === 0 ? (
              <Box sx={styles.emptyBox}>
                <AppText variant="bodySecondary">{t('bruno.navigation.empty_list')}</AppText>
              </Box>
            ) : (
              pending.map((item, i) => (
                <React.Fragment key={item.aisle}>
                  <AisleItem
                    aisleId={item.aisle}
                    statusText={mapAisleStatus(item.status, item.session_abortion_reason)}
                    layout={layout}
                  />
                  {i < pending.length - 1 && <Divider sx={{ mx: 3 }} />}
                </React.Fragment>
              ))
            )
          )}
        </Box>

        {/* Footer spacer */}
        <Box sx={styles.footer} />
      </Box>
    </BaseModal>
  );
}
